/**
 * Scroll the window to the top, and actually arrive there.
 *
 * BEN-141 amendment 1. The back-to-top control shipped as a one-liner —
 * `scrollTo({top: 0, behavior: reduced ? "auto" : "smooth"})` — and was **silently
 * dead** for a whole class of users. Smooth scrolling can be disabled
 * independently of `prefers-reduced-motion` (Chrome's Smooth Scrolling flag,
 * `--disable-smooth-scrolling`, some GTK setups). Reduced-motion users took the
 * `auto` path and were fine; everyone else took `smooth` — the only other path —
 * and where smooth is off, `scrollTo` does nothing at all. The click handler ran,
 * the page never moved, and nothing told the user why. Caught on the deployed
 * build in a Chrome reporting `prefers-reduced-motion: false` where
 * `behavior:"smooth"` is a no-op even on a detached element.
 *
 * Lives in `lib/` rather than beside the button because a component file may only
 * export components (`react-refresh/only-export-components`), and because the
 * assertion that matters is testable without mounting React: *when smooth does not
 * move the page, the page still ends up at the top*. The broken version called
 * `scrollTo` too, so "did it call scrollTo" is not the regression test.
 */

/** How long to let a smooth scroll prove itself before falling back to an instant
 *  one. Long enough that a working animation is visibly under way, short enough
 *  that a dead one doesn't leave the button feeling broken. */
export const SMOOTH_FALLBACK_MS = 400;

/**
 * Scroll the window to an arbitrary y, and actually arrive there.
 *
 * BEN-141 #5. Generalised from `scrollToTop` when the forecast section nav
 * needed the same guarantee for four mid-page targets: every reason the
 * back-to-top button was silently dead applies verbatim to a jump link, and a
 * jump link that does nothing is harder to notice than a back-to-top that does
 * nothing (you are already mid-page, so "nothing moved" reads as "I mis-tapped").
 *
 * The zero-progress guard generalises from `scrollY > 0 && scrollY >= startedAt`
 * to "we did not move AND we are not already there". The original inequality only
 * made sense for a target of 0, where all progress is downward-to-upward; a
 * mid-page target can be in either direction, so the honest test is exact
 * non-movement. Sub-pixel tolerance because a real scroll position is
 * fractional under a zoomed or fractionally-scaled viewport.
 */
export function scrollToY(y: number): void {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // Captured BEFORE the call: "did this move at all?" is measured against where
  // the page was when the user clicked. Reading it afterwards compares the page
  // against itself and fires the fallback even mid-animation.
  const startedAt = window.scrollY;
  window.scrollTo({ top: y, behavior: reduced ? "auto" : "smooth" });
  if (reduced) return; // the instant path already landed

  // The guard requires ZERO progress, not incomplete progress, so a real smooth
  // scroll still in flight is never truncated. It also has to exempt "already
  // there", or clicking the active section schedules a pointless second scroll.
  window.setTimeout(() => {
    const movedNothing = Math.abs(window.scrollY - startedAt) < 1;
    const notThereYet = Math.abs(window.scrollY - y) >= 1;
    if (movedNothing && notThereYet) {
      window.scrollTo(0, y);
    }
  }, SMOOTH_FALLBACK_MS);
}

export function scrollToTop(): void {
  scrollToY(0);
}
